import {
  StateMachine,
  StateNodeConfig,
  StateNodeType
} from '@people-science/state-machine';

export interface ResumablePluginFlags {
  type: string;
  /** see FormElementPlugin.effectOnMount */
  effectOnMount?: boolean;
}

/**
 * Whether a local draft may resume the form at `statePath`. The path is
 * resolved by the same StateMachine DynamicFormProvider builds from the
 * template, so name collisions and nesting behave exactly as at runtime. A
 * state is resumable only when a participant can actually sit on it: it is a
 * leaf (DynamicForm mounts the named node itself, never a descendant), none
 * of its elements act on mount (redirects and service calls run the moment
 * the section renders, so resuming there re-fires them), and if it is final
 * it still asks for input rather than auto-submitting.
 */
export const isResumableStatePath = (
  config: StateNodeConfig | undefined,
  statePath: string | undefined,
  elementPlugins: ResumablePluginFlags[]
): boolean => {
  if (!config || !statePath) return false;
  const machine = new StateMachine(config);
  const node = machine.getStateNode(statePath);
  if (!node || node === machine.stateNode || node.states.length > 0) {
    return false;
  }
  const elements: { type?: string; required?: boolean }[] =
    node.meta?.elements || [];
  if (node.type === StateNodeType.Final && !elements.some(e => e.required)) {
    return false;
  }
  return !elements.some(element =>
    elementPlugins.some(p => p.type === element.type && p.effectOnMount)
  );
};
